import * as styles from "./DateRecurrenceSchedule.css";

import type { JSX } from "react";

import {
  DatePicker,
  DatePickerContent,
  DatePickerTrigger,
  DatePickerValue,
} from "@frontend/components/atoms/DatePicker";
import {
  Field,
  FieldContent,
  FieldLabel,
} from "@frontend/components/atoms/Field";
import { NumberField } from "@frontend/components/atoms/NumberField";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@frontend/components/atoms/Select";
import { useLingui } from "@lingui/react/macro";

import type {
  RecurrenceEnd,
  RecurrenceEndMode,
} from "./DateRecurrencePicker.types";

interface DateRecurrenceScheduleProps {
  end: RecurrenceEnd;
  onEndChange: (end: RecurrenceEnd) => void;
  startDate?: string | null;
  onStartDateChange?: (value: string | null) => void;
  showEnd?: boolean;
  locale?: string;
}

export function DateRecurrenceSchedule({
  end,
  onEndChange,
  startDate,
  onStartDateChange,
  showEnd = false,
  locale,
}: DateRecurrenceScheduleProps): JSX.Element | null {
  const { t } = useLingui();

  if (!onStartDateChange && !showEnd) {
    return null;
  }

  const modes: { value: RecurrenceEndMode; label: string }[] = [
    { value: "never", label: t`Never` },
    { value: "until", label: t`Until` },
    // "After" alone is a date filter elsewhere, hence the context
    {
      value: "count",
      label: t({ message: "After", context: "recurrence end" }),
    },
  ];

  function changeMode(mode: RecurrenceEndMode): void {
    onEndChange({
      ...end,
      endMode: mode,
      until: "until" === mode ? (end.until ?? defaultUntil(startDate)) : null,
      count: "count" === mode ? Math.max(1, end.count || 1) : end.count,
    });
  }

  return (
    <div data-slot="date-recurrence-schedule" className={styles.schedule}>
      {onStartDateChange && (
        <Field size="sm" className={styles.starts}>
          <FieldLabel>{t`Starts`}</FieldLabel>

          <FieldContent>
            <DatePicker
              locale={locale}
              value={startDate ?? null}
              onValueChange={onStartDateChange}
            >
              <DatePickerTrigger>
                <DatePickerValue />
              </DatePickerTrigger>
              <DatePickerContent />
            </DatePicker>
          </FieldContent>
        </Field>
      )}

      {showEnd && (
        <Field size="sm" className={styles.ends}>
          <FieldLabel>{t`Ends`}</FieldLabel>

          <FieldContent>
            <div className={styles.end}>
              <Select
                items={modes}
                size="sm"
                value={end.endMode}
                onValueChange={(next) => changeMode(next as RecurrenceEndMode)}
              >
                <SelectTrigger variant="ghost" className={styles.endMode}>
                  <SelectValue />
                </SelectTrigger>

                <SelectContent>
                  {modes.map((mode) => (
                    <SelectItem key={mode.value} value={mode.value}>
                      {mode.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {"never" === end.endMode && (
                <span className={styles.endless}>{t`indefinitely`}</span>
              )}

              {"until" === end.endMode && (
                <DatePicker
                  layout="text"
                  locale={locale}
                  value={end.until}
                  onValueChange={(until) => onEndChange({ ...end, until })}
                >
                  <DatePickerTrigger>
                    <DatePickerValue />
                  </DatePickerTrigger>
                  <DatePickerContent />
                </DatePicker>
              )}

              {"count" === end.endMode && (
                <>
                  <NumberField
                    layout="text"
                    min={1}
                    max={999}
                    value={end.count}
                    onValueChange={(count) =>
                      onEndChange({ ...end, count: Math.max(1, count ?? 1) })
                    }
                  />

                  <span className={styles.unit}>{t`times`}</span>
                </>
              )}
            </div>
          </FieldContent>
        </Field>
      )}
    </div>
  );
}

function defaultUntil(startDate?: string | null): string {
  const start = startDate ? new Date(`${startDate}T00:00:00Z`) : new Date();

  start.setUTCFullYear(start.getUTCFullYear() + 1);

  return start.toISOString().slice(0, 10);
}
